import React from "react";
import { Link } from "react-router-dom";

import cargoBodyImg from "../../assets/images/Home/freezer-truck-service-chiller-van.webp";

export const chillerVanDubaiInvestmentParkSections = [
  {
    type: "cvd-hero",
    title: "Chiller Van Rental in Dubai Investment Park — Collection Timed to Your Production Batch",
    paragraphs: [
      "In Dubai Investment Park the production line sets the collection time, and our chiller van rental in DIP is booked against it.",
      "DIP runs on manufacturing time. Commercial bakeries, beverage bottlers, 3PL cold stores and central processing kitchens release output when a batch finishes, not when a delivery slot opens — so collection here is booked against the production schedule rather than the clock.",
      "Fast-response dispatch from our Al Barsha branch runs 30 to 40 minutes. Scheduled batch runs are staged at your dock ahead of that. Rates start at AED 300 a day self-drive. Contact our team with your batch timing and dock location to schedule collection.",
    ],
  },

  {
    type: "cvd-table",
    title: "Collection Is Timed to the Batch, Not the Slot",
    intro: [
      "A finished production batch is at its most vulnerable in the hour after it comes off the line. It is at temperature, it is packed, and it is sitting in a factory bay waiting for a vehicle.",
      <>That is why DIP bookings are staged rather than dispatched. For scheduled manufacturing runs, a pre-cooled van is backed into the loading bay <strong>before the batch completes</strong>, so loading begins the moment output is ready instead of starting with a phone call and a 30 to 40 minute wait.</>,
      "Production releases across the district run in two waves:",
    ],
    columns: ["Run", "Window", "What it covers"],
    rows: [
      ["Morning batch", "06:00 – 09:30", "Factory output reaching city retail and supermarket distribution before midday"],
      ["Afternoon batch", "14:30 – 18:00", "Output collected for overnight cold storage or inter-emirate transit"],
      ["Cross-dock intake", "08:00 – 16:30", "Standard warehouse receiving hours, between the two production waves"],
    ],
    notes: [
      "Pre-cooling matters differently here than elsewhere in Dubai. A van backing into an active factory bay in July meets the plant's own door opening, and the thermal exchange at that moment is larger than anything the drive produced. Vans on DIP runs are brought fully to temperature before docking rather than while waiting at the bay, so the cargo body absorbs the factory's heat load rather than fighting it from a warm start.",
    ],
    background: "#f8f9fa",
  },

  {
    type: "cvd-text-image",
    title: "What's Produced in Dubai Investment Park",
    paragraphs: [
      "Four categories account for most of the chilled volume leaving the district.",
      <><strong>Commercial food manufacturers and industrial bakeries</strong> — large-scale bakeries, packaged snack producers and sauce blenders shipping finished goods in bulk to distribution hubs.</>,
      <><strong>3PL cold storage and logistics centres</strong> — high-bay HACCP-certified facilities, primary FMCG warehouses and food distribution parks, running cross-dock transfers in and out.</>,
      <><strong>Dairy and beverage bottlers</strong> — juice, milk and plant-based beverage plants releasing daily batch output for citywide supermarket restocking. These are the most schedule-bound customers in DIP, because a bottling line finishing at 08:00 needs the van at 08:00.</>,
      <><strong>Central pre-cut produce and catering kitchens</strong> — high-volume processing facilities preparing pre-cut salads, sliced meats and ready-to-eat catering items, where the cut surface makes the cold chain window shorter than for whole product.</>,
      "Most DIP work is outbound. Where a run is inbound — raw material or packaging into a plant — it lands in the cross-dock window between the two production waves.",
    ],
    image: cargoBodyImg,
    imageAlt: "Pre-cooled chiller van cargo body for factory batch collection in Dubai Investment Park",
    reverse: false,
    altBg: true,
  },

  {
    type: "cvd-table",
    title: "DIP 1 and DIP 2 — Different Infrastructure, Different Van",
    description: "The two phases of the park were built two decades apart, and the loading reality differs accordingly.",
    columns: ["", "DIP 1", "DIP 2"],
    rows: [
      ["Business mix", "Mid-format warehouses, food processing units, central bakeries, showrooms", "High-bay 3PL cold stores, large processing plants, FMCG logistics parks"],
      ["Roads", "Gated compounds, medium-width internal roads", "Wide boulevards, modern industrial campuses"],
      ["Docks", "Ground-level roller shutters, level loading aprons, covered bays", "Elevated hydraulic dock levellers, 10m high-bay warehouses"],
      ["Security", "Gate barrier, destination verification", "24/7 gated security check-in"],
      ["Van assigned", <><strong>Standard roof</strong> — agile for mid-format bays</>, <><strong>High roof</strong> — maximises crate and pallet height on bulk runs</>],
    ],
    notes: [
      "The split matters when a booking is placed. A high roof van sent to a DIP 1 ground-level roller shutter gains nothing from the extra interior height and loses manoeuvrability in a medium-width compound. A standard roof van sent to a DIP 2 hydraulic leveller for a bulk run wastes a trip's worth of capacity.",
      "Confirming the phase and the dock type at booking decides which configuration we send.",
    ],
    background: "#f8f9fa",
  },

  {
    type: "cvd-text-only",
    title: "Same Corridor as Jebel Ali, Different Procedure",
    paragraphs: [
      "DIP and Jebel Ali sit 15 to 20 minutes apart on the same south-western logistics corridor along the E311 and Expo Road. Vans staged in that loop cover both districts in a single run, which is how most multi-stop bookings across the two are handled.",
      "What separates them is paperwork.",
      <><strong>DIP is mainland Dubai.</strong> No bonded customs documentation, no free zone gate pass, no customs exit permit. Entry to the DIP 1 and DIP 2 industrial parks requires a stop at the main security barrier for destination verification, with the driver presenting a commercial manifest or company ID — a few minutes, not the 20 to 40 that JAFZA gate processing adds.</>,
      "For a business operating across both, that difference changes the sequencing. A run collecting in DIP and delivering into the free zone absorbs the gate time once at the JAFZA end rather than at both. Scheduling the DIP leg first is usually the faster order.",
    ],
  },

  {
    type: "cvd-table",
    title: "Chiller Van Rental Rates From Dubai Investment Park",
    description: "Rates cover runs originating in DIP. Transit times are door to door before any dock or gate queue.",
    columns: ["Destination", "Self-drive", "With driver", "Off-peak", "Peak"],
    rows: [
      ["Jebel Ali — JAFZA", "AED 300", "AED 350", "15–20 min", "25–35 min"],
      ["Dubai South / DWC", "AED 300", "AED 350", "15–20 min", "25–30 min"],
      ["Al Barsha", "AED 300", "AED 350", "25–35 min", "40–50 min"],
      ["Dubai Marina / JBR", "AED 350", "AED 400", "25–30 min", "40–50 min"],
      ["Al Quoz Industrial", "AED 350", "AED 400", "25–35 min", "40–50 min"],
      ["Business Bay / Downtown", "AED 350", "AED 400", "30–40 min", "50–60 min"],
      ["Ras Al Khor", "AED 350", "AED 400", "30–40 min", "50–60 min"],
    ],
    notes: [
      <>All figures are starting rates, confirmed against cargo type and duration before booking. Weekly and monthly terms carry lower per-day rates, listed on <Link to="/chiller-van-for-rent" className="internal-link">our full Dubai chiller van service page</Link>. UAE VAT applies where applicable.</>,
      "Southbound runs to Jebel Ali and Dubai South are the shortest on this corridor. Where a consignment can route south rather than back into the city, it saves roughly half the transit. which works for supermarket distribution but not for a retail receiving window that closes at 09:30.",
    ],
  },

  {
    type: "cvd-faq",
    title: "Frequently Asked Questions",
    faqs: [
      {
        q: "Can you stage a van at our factory dock before the batch is ready?",
        a: "Yes, and that is how most scheduled DIP runs work. Tell us when the batch completes and the van is backed in and at temperature before then, so loading starts on output rather than after a dispatch call.",
      },
      {
        q: "Do I need a gate pass for a chiller van for rent in DIP?",
        a: "No. Dubai Investment Park is mainland Dubai, so there is no bonded customs paperwork or free zone clearance. The DIP 1 and DIP 2 compounds have a security barrier where the driver presents a commercial manifest or company ID for destination verification — a short stop, not a clearance process.",
      },
      {
        q: "Which van suits a DIP 2 high-bay dock?",
        a: "A high roof unit. DIP 2 facilities run elevated hydraulic levellers into 10-metre high-bay warehouses, where crate and pallet height is what decides how much moves per trip. DIP 1's ground-level roller shutters and level aprons suit a standard roof van better.",
      },
    ],
  },

  {
    type: "cta",
    title: "Book a Chiller Van in Dubai Investment Park",
    paragraphs: [
      "Five chiller vans cover DIP 1, DIP 2 and the wider south-western corridor, staged against production schedules rather than dispatched on request.",
      <>Tell us your phase, your dock type and when your batch completes. For recurring production runs we hold a slot against your line schedule rather than rebooking each collection. Full fleet detail is on our <Link to="/chiller-van-for-rent" className="internal-link">chiller van fleet specifications page</Link>.</>,
    ],
  },
];
